import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RefreshCw, Clock, MapPin, Search, X, Navigation, ArrowLeft, UserPlus, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import * as crmBackend from '../services/crmBackendService';
import TechnicianMultiSelect from '../components/TechnicianMultiSelect';
import AppVersion from '../components/AppVersion';
import { allowLeave } from '../lib/leaveGuard';

const DEFAULT_CENTER = { lat: 33.1032, lng: -96.6706 };
const MAP_HEIGHT = 420;

const minutesAgo = (iso) => {
  if (!iso) return null;
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diff < 1) return 'just now';
  if (diff === 1) return '1 min ago';
  return `${diff} min ago`;
};

function validTechs(techs) {
  return (techs || []).filter((t) => Number.isFinite(Number(t.lat)) && Number.isFinite(Number(t.lng)));
}

function distanceMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatMiles(mi) {
  if (mi == null || !Number.isFinite(mi)) return '—';
  if (mi < 0.1) return '< 0.1 mi';
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
}

function firstAddressLine(addr) {
  if (!addr) return '';
  return String(addr).split('\n')[0].trim();
}

// Location rows carry the tech's login name; assignments store the technician id (see TECHNICIANS).
function technicianValueFor(techName) {
  const n = String(techName || '').trim().toLowerCase();
  if (!n) return '';
  const match = (crmBackend.TECHNICIANS || []).find(
    (t) => t.value.toLowerCase() === n || t.value.split('@')[0].toLowerCase() === n || t.label.toLowerCase() === n
  );
  return match ? match.value : '';
}

const LEAFLET_CSS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js';
let leafletLoadPromise = null;

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletLoadPromise) return leafletLoadPromise;
  leafletLoadPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => {
      if (window.L) {
        resolve(window.L);
        return;
      }
      leafletLoadPromise = null;
      reject(new Error('Leaflet loaded without a global L instance'));
    };
    script.onerror = () => {
      leafletLoadPromise = null;
      reject(new Error('Could not load map library'));
    };
    document.body.appendChild(script);
  });
  return leafletLoadPromise;
}

function makeDotIcon(color, size = 14) {
  return (L) =>
    L.divIcon({
      className: '',
      html: `<div style="background:${color};width:${size}px;height:${size}px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45)"></div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
}

function FieldMap({ techs, target }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef([]);
  const [mapError, setMapError] = useState('');

  const techList = useMemo(() => validTechs(techs), [techs]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const L = await loadLeaflet();
        if (cancelled || !containerRef.current) return;
        setMapError('');

        if (!mapRef.current) {
          mapRef.current = L.map(containerRef.current, { scrollWheelZoom: true }).setView(
            [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng],
            10
          );
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap',
            maxZoom: 19,
          }).addTo(mapRef.current);
        }

        layersRef.current.forEach((layer) => layer.remove());
        layersRef.current = [];

        const jobIcon = makeDotIcon('#ea580c', 16)(L);
        const techIcon = makeDotIcon('#059669', 13)(L);
        const boundsPoints = [];

        if (target) {
          const jobLatLng = [target.lat, target.lng];
          boundsPoints.push(jobLatLng);
          const jobMarker = L.marker(jobLatLng, { icon: jobIcon, zIndexOffset: 1000 })
            .addTo(mapRef.current)
            .bindPopup(
              `<strong>Job / address</strong><br/>${target.jobLabel || ''}<br/>${target.address || ''}`
            );
          layersRef.current.push(jobMarker);
        }

        techList.forEach((tech) => {
          const lat = Number(tech.lat);
          const lng = Number(tech.lng);
          const latLng = [lat, lng];
          boundsPoints.push(latLng);

          if (target) {
            const dist = formatMiles(distanceMiles(target.lat, target.lng, lat, lng));
            const line = L.polyline([latLng, [target.lat, target.lng]], {
              color: '#64748b',
              weight: 2,
              dashArray: '7, 9',
              opacity: 0.75,
            }).addTo(mapRef.current);
            layersRef.current.push(line);
          }

          const marker = L.marker(latLng, { icon: techIcon })
            .addTo(mapRef.current)
            .bindPopup(
              `<strong>${tech.tech_name || 'Tech'}</strong>${
                target
                  ? `<br/>${formatMiles(distanceMiles(target.lat, target.lng, lat, lng))} from job`
                  : ''
              }${tech.job_id ? `<br/>Job: ${tech.job_id}` : ''}`
            );
          layersRef.current.push(marker);
        });

        if (boundsPoints.length > 1) {
          mapRef.current.fitBounds(boundsPoints, { padding: [40, 40], maxZoom: 14 });
        } else if (boundsPoints.length === 1) {
          mapRef.current.setView(boundsPoints[0], 14);
        } else {
          mapRef.current.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], 10);
        }

        setTimeout(() => mapRef.current?.invalidateSize(), 150);
      } catch (err) {
        console.error('Map init failed', err);
        if (!cancelled) setMapError('Map could not load. Check your internet connection and refresh.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [techList, target]);

  useEffect(
    () => () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      layersRef.current = [];
    },
    []
  );

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className="w-full rounded-lg bg-gray-100 z-0"
        style={{ height: MAP_HEIGHT, minHeight: 320 }}
      />
      {mapError && (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 bg-gray-100 rounded-lg px-4 text-center">
          {mapError}
        </p>
      )}
      <div className="absolute bottom-2 left-2 z-[1000] flex gap-3 rounded-md bg-white/95 border border-gray-200 px-2 py-1 text-xs shadow-sm">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#ea580c] border border-white shadow" />
          Job
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#059669] border border-white shadow" />
          Tech
        </span>
        {target && (
          <span className="text-gray-400 hidden sm:inline">— dashed line = distance</span>
        )}
      </div>
    </div>
  );
}

function TechRow({ tech, rank, target, drivingEta, assignTo, assigned, assigning, onAssign }) {
  const dist = target
    ? distanceMiles(target.lat, target.lng, Number(tech.lat), Number(tech.lng))
    : null;

  return (
    <div
      className={`flex items-center justify-between p-3 border rounded-lg ${
        rank === 1 && target ? 'border-blue-300 bg-blue-50/50' : ''
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
            rank === 1 && target ? 'bg-blue-600 text-white' : 'bg-green-100 text-green-700'
          }`}
        >
          {rank === 1 && target ? '★' : tech.tech_name?.[0]?.toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 truncate">{tech.tech_name}</p>
          <p className="text-xs text-gray-500 truncate">
            {Number(tech.lat).toFixed(5)}, {Number(tech.lng).toFixed(5)}
            {tech.job_id && <span className="ml-2 text-blue-600">· {tech.job_id}</span>}
          </p>
        </div>
      </div>
      <div className="text-right shrink-0 ml-2">
        {target && (
          <p className="text-sm font-semibold text-blue-700">{formatMiles(dist)}</p>
        )}
        {drivingEta && (
          <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
            <Navigation className="w-3 h-3" />
            {drivingEta}
          </p>
        )}
        <Badge className="bg-green-100 text-green-700 text-xs mt-1">Active</Badge>
        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1 justify-end">
          <Clock className="w-3 h-3" />
          {minutesAgo(tech.updated_at) || '—'}
        </p>
        {assignTo && (
          <Button
            type="button"
            size="sm"
            variant={assigned ? 'outline' : 'default'}
            className="mt-1 mb-1 h-7 px-2 text-xs"
            disabled={assigned || assigning}
            onClick={onAssign}
          >
            {assigned ? <Check className="w-3 h-3 mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
            {assigned ? 'Assigned' : `Assign to ${assignTo}`}
          </Button>
        )}
        <a
          href={`https://maps.google.com/?q=${tech.lat},${tech.lng}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-blue-600 hover:underline"
        >
          Open in Maps
        </a>
      </div>
    </div>
  );
}

const TechMap = ({ embedded = false }) => {
  const [techs, setTechs] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState('');

  const [addrQuery, setAddrQuery] = useState('');
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const skipAddrSuggest = useRef(false);

  const [target, setTarget] = useState(null);
  const [drivingEtas, setDrivingEtas] = useState({});

  // Assign a technician to the selected job without leaving the map.
  const [assignValue, setAssignValue] = useState('');
  const [assignSaved, setAssignSaved] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignMsg, setAssignMsg] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await crmBackend.getTechLocations();
      setTechs(result.data || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Tech locations fetch failed', err);
      setError('Could not load technician locations.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const [estRes, woRes] = await Promise.all([
        crmBackend.getEstimates(),
        crmBackend.getWorkOrders(),
      ]);
      const fromEst = (estRes.data || [])
        .filter((e) => firstAddressLine(e.custom_service_address))
        .map((e) => ({
          id: `est:${e.name}`,
          jobId: e.name,
          label: `${e.name} — ${e.customer_name || e.party_name || 'Estimate'}`,
          address: firstAddressLine(e.custom_service_address),
          estimateName: e.name,
          technician: e.custom_technician || '',
        }));
      const fromWo = (woRes.data || [])
        .filter((w) => firstAddressLine(w.service_address))
        .map((w) => ({
          id: `wo:${w.wo_id}`,
          jobId: w.wo_id,
          label: `WO ${w.wo_id} — ${w.customer_name || w.source_estimate || 'Work order'}`,
          address: firstAddressLine(w.service_address),
          estimateName: w.source_estimate || '',
          technician: w.technician || '',
        }));
      setJobs([...fromWo, ...fromEst]);
    } catch (err) {
      console.error('Failed to load jobs for map', err);
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const setTargetFromGeocode = useCallback(async (address, meta = {}) => {
    const trimmed = (address || '').trim();
    if (!trimmed) return;
    setGeocoding(true);
    setError('');
    try {
      const data = await crmBackend.geocodeAddress(trimmed);
      setTarget({
        lat: data.lat,
        lng: data.lng,
        address: data.formatted_address || trimmed,
        ...meta,
      });
      setAddrQuery(data.formatted_address || trimmed);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not find that address on the map.';
      setError(typeof msg === 'string' ? msg : 'Could not find that address on the map.');
    } finally {
      setGeocoding(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    loadJobs();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh, loadJobs]);

  useEffect(() => {
    const q = addrQuery.trim();
    if (skipAddrSuggest.current) {
      skipAddrSuggest.current = false;
      return undefined;
    }
    if (q.length < 4) {
      setAddrSuggestions([]);
      setAddrOpen(false);
      return undefined;
    }
    setAddrLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await crmBackend.suggestAddress(q);
        setAddrSuggestions(data?.suggestions || []);
        setAddrOpen((data?.suggestions || []).length > 0);
      } catch {
        setAddrSuggestions([]);
        setAddrOpen(false);
      } finally {
        setAddrLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [addrQuery]);

  useEffect(() => {
    if (!target?.address) {
      setDrivingEtas({});
      return undefined;
    }
    let cancelled = false;
    const ranked = validTechs(techs)
      .map((t) => ({
        ...t,
        dist: distanceMiles(target.lat, target.lng, Number(t.lat), Number(t.lng)),
      }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 3);

    (async () => {
      const etas = {};
      await Promise.all(
        ranked.map(async (t) => {
          try {
            const eta = await crmBackend.getEtaFromCoords(t.lat, t.lng, target.address);
            if (!cancelled && eta?.eta_text) {
              etas[t.tech_name] = eta.eta_text;
            }
          } catch {
            /* driving ETA optional — needs GOOGLE_MAPS_API_KEY */
          }
        })
      );
      if (!cancelled) setDrivingEtas(etas);
    })();

    return () => {
      cancelled = true;
    };
  }, [target, techs]);

  const rankedTechs = useMemo(() => {
    const valid = validTechs(techs);
    if (!target) return valid.map((t, i) => ({ ...t, rank: i + 1, distanceMiles: null }));
    return valid
      .map((t) => ({
        ...t,
        distanceMiles: distanceMiles(target.lat, target.lng, Number(t.lat), Number(t.lng)),
      }))
      .sort((a, b) => a.distanceMiles - b.distanceMiles)
      .map((t, i) => ({ ...t, rank: i + 1 }));
  }, [techs, target]);

  const handleJobSelect = async (jobKey) => {
    setSelectedJobId(jobKey);
    if (!jobKey) {
      setTarget(null);
      return;
    }
    const job = jobs.find((j) => j.id === jobKey);
    if (!job) return;
    skipAddrSuggest.current = true;
    setAssignMsg(null);
    setAssignValue(job.technician || '');
    setAssignSaved(job.technician || '');
    if (job.estimateName) {
      // The job list can be stale — read the estimate's current assignment.
      crmBackend.getEstimateDetail(job.estimateName).then((res) => {
        // ERPNext omits empty fields, so a missing value means "nobody assigned".
        const doc = res?.data;
        if (doc && doc.name) {
          setAssignValue(doc.custom_technician || '');
          setAssignSaved(doc.custom_technician || '');
        }
      }).catch(() => {});
    }
    await setTargetFromGeocode(job.address, {
      jobId: job.jobId,
      jobLabel: job.label,
      estimateName: job.estimateName || '',
    });
  };

  // The assignment follows the job picked in the dropdown, not the map pin, so it still works when the
  // address can't be placed on the map.
  const selectedJob = useMemo(() => jobs.find((j) => j.id === selectedJobId) || null, [jobs, selectedJobId]);
  const assignEstimate = selectedJob?.estimateName || '';

  const saveAssignment = async (value) => {
    if (!assignEstimate) return;
    setAssignSaving(true);
    setAssignMsg(null);
    try {
      await crmBackend.updateEstimate(assignEstimate, { custom_technician: value });
      setAssignValue(value);
      setAssignSaved(value);
      setJobs((list) => list.map((j) => (j.estimateName === assignEstimate ? { ...j, technician: value } : j)));
      setAssignMsg({
        ok: true,
        text: value
          ? `Assigned ${crmBackend.getTechnicianLabel(value)} to ${assignEstimate}.`
          : `Cleared the technician on ${assignEstimate}.`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setAssignMsg({ ok: false, text: typeof detail === 'string' ? detail : 'Could not save the assignment.' });
    } finally {
      setAssignSaving(false);
    }
  };

  const handleAssignSave = () => saveAssignment(assignValue);

  // One tap on a technician in the list adds them to the selected job.
  const handleQuickAssign = (techValue) => {
    const current = crmBackend.parseTechnicians(assignSaved);
    if (current.includes(techValue)) return;
    saveAssignment(crmBackend.formatTechnicians([...current, techValue]));
  };

  const goBack = () => {
    if (window.history.length > 1) window.history.back();
    else { allowLeave(); window.location.assign('/dashboard'); }
  };

  const handleLocateAddress = () => {
    setSelectedJobId('');
    setTargetFromGeocode(addrQuery);
  };

  const clearTarget = () => {
    setTarget(null);
    setSelectedJobId('');
    setAddrQuery('');
    setDrivingEtas({});
  };

  const selectSuggestion = (s) => {
    skipAddrSuggest.current = true;
    setAddrQuery(s.description);
    setAddrOpen(false);
    setAddrSuggestions([]);
    setSelectedJobId('');
    setTargetFromGeocode(s.description);
  };

  return (
    <div className="space-y-6">
      <div className={`flex items-center justify-between ${embedded ? 'mb-2' : ''}`}>
        {!embedded && (
          <div>
            <Button type="button" variant="ghost" size="sm" onClick={goBack} className="-ml-2 mb-1 text-gray-600">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h2 className="text-2xl font-bold text-gray-900">Field Technicians</h2>
            <p className="text-gray-500 mt-1">
              Live locations · updates every 30 s
              {lastRefresh && (
                <span className="ml-2 text-xs text-gray-400">
                  Last: {lastRefresh.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
        )}
        {embedded && (
          <p className="text-sm text-gray-500">
            Updates every 30 s
            {lastRefresh && (
              <span className="ml-2 text-xs text-gray-400">
                Last: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </p>
        )}
        <Button onClick={refresh} disabled={loading} variant="outline" size="sm" className={embedded ? 'ml-auto' : ''}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Find nearest tech &amp; assign</CardTitle>
          <CardDescription>Pick a job to see who is closest and assign a technician, or type an address</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Job / estimate</label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={selectedJobId}
                disabled={jobsLoading || geocoding}
                onChange={(e) => handleJobSelect(e.target.value)}
              >
                <option value="">{jobsLoading ? 'Loading jobs…' : 'Select a job…'}</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.label}
                    {j.estimateName ? ` · ${j.technician ? crmBackend.getTechnicianLabel(j.technician) : 'unassigned'}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Or search address</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    value={addrQuery}
                    onChange={(e) => {
                      setAddrQuery(e.target.value);
                      setSelectedJobId('');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleLocateAddress()}
                    placeholder="123 Main St, Allen TX"
                    disabled={geocoding}
                  />
                  {addrOpen && addrSuggestions.length > 0 && (
                    <ul className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow-lg max-h-48 overflow-auto">
                      {addrSuggestions.map((s) => (
                        <li key={s.place_id}>
                          <button
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                            onClick={() => selectSuggestion(s)}
                          >
                            {s.description}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleLocateAddress}
                  disabled={geocoding || !addrQuery.trim()}
                >
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              {addrLoading && <p className="text-xs text-gray-400 mt-1">Searching…</p>}
            </div>
          </div>

          {target && (
            <div className="flex items-start justify-between gap-3 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
              <div className="flex items-start gap-2 min-w-0">
                <MapPin className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-blue-900 truncate">
                    {target.jobLabel || 'Search location'}
                    {target.jobId && <span className="text-blue-600 ml-1">({target.jobId})</span>}
                  </p>
                  <p className="text-xs text-blue-700 truncate">{target.address}</p>
                  {rankedTechs.length > 0 && (
                    <p className="text-xs text-blue-600 mt-1">
                      Closest: <strong>{rankedTechs[0].tech_name}</strong> ({formatMiles(rankedTechs[0].distanceMiles)})
                    </p>
                  )}
                </div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={clearTarget} className="shrink-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          {!selectedJob && (
            <div className="rounded-lg border border-dashed px-3 py-3">
              <p className="text-xs font-medium text-gray-700">Assign technician</p>
              <p className="text-xs text-gray-500 mt-1">Select a job above, then choose the technician here.</p>
            </div>
          )}
          {assignEstimate && (
            <div className="rounded-lg border px-3 py-3 space-y-2">
              <TechnicianMultiSelect
                label={`Assign technician — ${assignEstimate}`}
                value={assignValue}
                onChange={(val) => { setAssignValue(val); setAssignMsg(null); }}
                disabled={assignSaving}
              />
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAssignSave}
                  disabled={assignSaving || assignValue === assignSaved}
                >
                  {assignSaving ? 'Saving…' : 'Save assignment'}
                </Button>
                {assignMsg && (
                  <p className={`text-xs ${assignMsg.ok ? 'text-green-700' : 'text-red-600'}`}>{assignMsg.text}</p>
                )}
              </div>
            </div>
          )}
          {selectedJob && !assignEstimate && (
            <p className="text-xs text-gray-500">This work order has no linked estimate, so a technician can't be assigned from the map.</p>
          )}
        </CardContent>
      </Card>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      <Card>
        <CardContent className="p-0 overflow-hidden rounded-lg">
          <FieldMap techs={techs} target={target} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{target ? 'Technicians by distance' : 'Active Technicians'}</CardTitle>
          <CardDescription>
            {rankedTechs.length} tech{rankedTechs.length !== 1 ? 's' : ''} reporting location
            {target && rankedTechs.length > 0 && ' · sorted nearest first'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rankedTechs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">
              No technicians have reported their location yet.
              Location is sent automatically when a tech is signed in on the field app.
            </p>
          ) : (
            <div className="space-y-3">
              {rankedTechs.map((tech) => (
                <TechRow
                  key={tech.tech_name}
                  tech={tech}
                  rank={tech.rank}
                  target={target}
                  drivingEta={drivingEtas[tech.tech_name]}
                  assignTo={assignEstimate && technicianValueFor(tech.tech_name) ? assignEstimate : ''}
                  assigned={crmBackend.parseTechnicians(assignSaved).includes(technicianValueFor(tech.tech_name))}
                  assigning={assignSaving}
                  onAssign={() => handleQuickAssign(technicianValueFor(tech.tech_name))}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <AppVersion />
    </div>
  );
};

export default TechMap;
